import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Target, Clock, Link as LinkIcon } from 'lucide-react';

// `embedded` renders the form and list WITHOUT the page shell, so the Invite page can host
// it as a tab. Same component either way — a second copy of a lead form is a second place
// for the fields to drift.
const PartnerLeads = ({ embedded = false }) => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    contact_mobile: '+91 ',
    contact_email: '',
    city: '',
    industry: '',
    estimated_budget: '',
    notes: '',
    source: 'whatsapp',
    proposed_arrangement: ''
  });

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/partner/leads`, {
        headers: getAuthHeaders()
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    if (!formData.industry) {
      toast.error('Please select an industry');
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        estimated_budget: parseFloat(formData.estimated_budget) || 0
      };

      await axios.post(`${API_URL}/api/partner/leads`, payload, {
        headers: getAuthHeaders()
      });
      
      toast.success('Lead submitted successfully!');
      setFormData({
        business_name: '',
        contact_name: '',
        contact_mobile: '+91 ',
        contact_email: '',
        city: '',
        industry: '',
        proposed_arrangement: '',
        estimated_budget: '',
        notes: '',
        source: 'whatsapp'
      });
      fetchLeads();
    } catch (error) {
      console.error('Failed to submit lead:', error);
      toast.error('Failed to submit lead. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getPipelineStageLabel = (stage) => {
    const labels = {
      new: 'New',
      contacted: 'Contacted',
      demo_scheduled: 'Demo Scheduled',
      trial: 'Trial',
      converted: 'Converted',
      churned: 'Churned'
    };
    return labels[stage] || stage;
  };

  const getPipelineStageColor = (stage) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700 border-blue-200',
      contacted: 'bg-amber-100 text-amber-700 border-amber-200',
      demo_scheduled: 'bg-violet-100 text-violet-700 border-violet-200',
      trial: 'bg-orange-100 text-orange-700 border-orange-200',
      converted: 'bg-green-100 text-green-700 border-green-200',
      churned: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[stage] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const body = (
      <div className="space-y-6" data-testid="partner-leads">
        {!embedded && (
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">Submit a Lead</h1>
            <p className="text-sm text-[#64748B] mt-1">
              Submit new referrals and track their progress through the pipeline
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Lead Form */}
          <Card className="bg-white border-[#E2E2DF]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Plus className="w-5 h-5 text-[#ef785b]" />
                New Lead
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Business Name *</Label>
                    <Input
                      value={formData.business_name}
                      onChange={(e) => handleChange('business_name', e.target.value)}
                      placeholder="Company name"
                      className="mt-1.5 h-10 border-[#E2E2DF]"
                      required
                      data-testid="lead-business-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Contact Name *</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => handleChange('contact_name', e.target.value)}
                      placeholder="Contact person"
                      className="mt-1.5 h-10 border-[#E2E2DF]"
                      required
                      data-testid="lead-contact-name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Mobile *</Label>
                    <Input
                      value={formData.contact_mobile}
                      onChange={(e) => handleChange('contact_mobile', e.target.value)}
                      placeholder="+91 98765 43210"
                      className="mt-1.5 h-10 border-[#E2E2DF]"
                      required
                      data-testid="lead-mobile"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Email</Label>
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => handleChange('contact_email', e.target.value)}
                      placeholder="contact@company.com"
                      className="mt-1.5 h-10 border-[#E2E2DF]"
                      data-testid="lead-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">City *</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="City"
                      className="mt-1.5 h-10 border-[#E2E2DF]"
                      required
                      data-testid="lead-city"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Industry *</Label>
                    <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                      <SelectTrigger className="mt-1.5 h-10 border-[#E2E2DF]" data-testid="lead-industry">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ecommerce">E-commerce</SelectItem>
                        <SelectItem value="saas">SaaS</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="services">Services</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Estimated Monthly Budget</Label>
                    <Input
                      type="number"
                      value={formData.estimated_budget}
                      onChange={(e) => handleChange('estimated_budget', e.target.value)}
                      placeholder="₹15,000"
                      className="mt-1.5 h-10 border-[#E2E2DF]"
                      data-testid="lead-budget"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Source</Label>
                    <Select value={formData.source} onValueChange={(v) => handleChange('source', v)}>
                      <SelectTrigger className="mt-1.5 h-10 border-[#E2E2DF]" data-testid="lead-source">
                        <SelectValue placeholder="How did you connect?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="whatsapp">WhatsApp Introduction</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="in-person">In-person Meeting</SelectItem>
                        <SelectItem value="call">Phone Call</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* WHICH ARRANGEMENT. This is the only place solution or affiliate can be
                    reached at all — every other door defaults to referral. It is a request:
                    the rate does not move until Expedify approves it. */}
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A]">
                    How will this client be engaged?
                  </Label>
                  <Select
                    value={formData.proposed_arrangement}
                    onValueChange={(v) => handleChange('proposed_arrangement', v)}
                  >
                    <SelectTrigger className="mt-1.5 h-10 border-[#E2E2DF]" data-testid="lead-arrangement">
                      <SelectValue placeholder="Standard referral (default)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="referral">Referral — you introduce, we do the rest</SelectItem>
                      <SelectItem value="solution">Solution — you deliver and support them</SelectItem>
                      <SelectItem value="affiliate">Affiliate — you send volume, we take it from there</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#64748B] mt-1.5">
                    A request, not a setting. Expedify confirms it when the client converts —
                    until then the standard referral rate applies.
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A]">Notes / Context</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Any additional context about the lead..."
                    className="mt-1.5 w-full min-h-[80px] rounded-lg border border-[#E2E2DF] px-3 py-2 text-sm focus:ring-2 focus:ring-[#ef785b]/20 focus:border-[#ef785b]"
                    data-testid="lead-notes"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-[#ef785b] hover:bg-[#D44017] text-white"
                  data-testid="submit-lead"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Submitting...
                    </span>
                  ) : (
                    <>
                      <Target className="w-4 h-4 mr-2" />
                      Submit Lead
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Recent Leads */}
          <Card className="bg-white border-[#E2E2DF]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="w-5 h-5 text-[#64748B]" />
                Your Submitted Leads
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef785b]"></div>
                </div>
              ) : leads.length === 0 ? (
                <div className="text-center py-8 text-[#64748B]">
                  <Target className="w-12 h-12 mx-auto mb-3 text-[#E2E2DF]" />
                  <p>No leads submitted yet.</p>
                  <p className="text-sm">Submit your first lead using the form.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {leads.map((lead, index) => (
                    <div key={lead.id} className="p-4 bg-[#FAFAF8] rounded-lg border border-[#E2E2DF]" data-testid={`lead-item-${index}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{lead.business_name}</p>
                          <p className="text-sm text-[#64748B]">{lead.contact_name} • {lead.city}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {lead.referred_by_link && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200 flex items-center gap-1">
                              <LinkIcon className="w-3 h-3" />
                              Via Link
                            </span>
                          )}
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getPipelineStageColor(lead.pipeline_stage)}`}>
                            {getPipelineStageLabel(lead.pipeline_stage)}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-xs text-[#64748B]">
                        <span>{lead.industry}</span>
                        {lead.estimated_budget > 0 && (
                          <span>Your estimate {formatCurrency(lead.estimated_budget)}/mo</span>
                        )}
                        <span>{new Date(lead.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                      </div>
                      {lead.deal_won && lead.deal_value > 0 && (
                        <div className={`mt-2 rounded-lg border px-3 py-2 ${lead.pipeline_stage === 'converted' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`lead-deal-${index}`}>
                          <div className="flex items-center gap-2 text-xs font-medium">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${lead.pipeline_stage === 'converted' ? 'bg-green-500' : 'bg-amber-500'}`}></span>
                            <span className={lead.pipeline_stage === 'converted' ? 'text-green-700' : 'text-amber-700'}>
                              Deal {formatCurrency(lead.deal_value)}{lead.deal_plan ? ` · ${lead.deal_plan} plan` : ''}
                              {lead.pipeline_stage === 'converted' ? ' · commission reconciled' : ' recorded — awaiting conversion'}
                            </span>
                          </div>
                          {lead.pipeline_stage !== 'converted' && (
                            <p className="mt-1 text-xs text-[#64748B]">You'll earn commission once this client is marked converted.</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
  );

  return embedded ? body : <DashboardLayout>{body}</DashboardLayout>;
};

export default PartnerLeads;
